import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Sparkles, Newspaper } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, TYPE_LABEL, ACCESS_LABEL } from "@/lib/api";
import { ArticleForm } from "@/components/admin/ArticleForm";

export const AdminArticles = () => {
  const [items, setItems] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busyId, setBusyId] = useState("");
  const load = () => api.get("/admin/articles").then((r) => setItems(r.data));
  useEffect(() => { load(); api.get("/admin/sponsors").then((r) => setSponsors(r.data)); }, []);

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    await api.delete(`/admin/articles/${a.id}`); toast.success("Article deleted"); load();
  };
  const regen = async (a) => {
    setBusyId(a.id);
    try { await api.post(`/admin/articles/${a.id}/takeaways`); toast.success("AI takeaways regenerated"); load(); } catch (e) { toast.error(formatApiError(e)); }
    setBusyId("");
  };
  const [runs, setRuns] = useState([]);
  const loadRuns = () => api.get("/admin/daily-article/runs").then((r) => setRuns(r.data));
  useEffect(() => { loadRuns(); }, []);
  const writeDaily = async () => {
    try {
      await api.post("/admin/daily-article");
      toast.success("Writing today's investigation. It appears here in about 2 minutes.");
      const started = Date.now();
      const poll = setInterval(async () => {
        const { data } = await api.get("/admin/daily-article/runs");
        setRuns(data);
        if (data[0]?.status !== "writing" || Date.now() - started > 8 * 60000) {
          clearInterval(poll);
          load();
          if (data[0]?.status === "published") toast.success(`Published: ${data[0].title}`);
          if (data[0]?.status === "failed") toast.error("The daily investigation could not be written. Try again shortly.");
        }
      }, 15000);
      loadRuns();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const last = runs[0];
  const sponsorName = (id) => sponsors.find((s) => s.id === id)?.name;

  return (
    <div data-testid="admin-articles">
      <div className="flex justify-between items-center">
        <p className="text-sm text-[#575757]">{items.length} published pieces</p>
        <div className="flex gap-2">
        <button data-testid="admin-write-daily-btn" onClick={writeDaily} className="flex items-center gap-2 border border-[#0A0A0A] px-5 py-2.5 text-sm hover:bg-[#0A0A0A] hover:text-white transition-colors"><Newspaper size={16} /> Write today's investigation now</button>
        <button data-testid="admin-create-article-btn" onClick={() => setEditing({})} className="flex items-center gap-2 bg-[#E5332A] text-white px-5 py-2.5 text-sm hover:bg-[#B8241C]"><Plus size={16} /> New article</button>
        </div>
      </div>
      <p className="mt-3 text-xs text-[#575757]" data-testid="admin-daily-status">
        Daily investigation publishes automatically at 6:30am UK.{" "}
        {last && <>Last run: <b className={last.status === "failed" ? "text-[#E5332A]" : "text-[#0A0A0A]"}>{last.status}</b>{last.title ? ` · ${last.title}` : ""}{last.error ? ` · ${last.error}` : ""}</>}
      </p>
      <div className="mt-6 border border-[#E7E7E7] bg-white divide-y divide-[#E7E7E7]">
        {items.map((a) => (
          <div key={a.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3 justify-between" data-testid={`admin-article-row-${a.slug}`}>
            <div className="min-w-0">
              <p className="overline !text-[9px] text-[#E5332A]">{TYPE_LABEL[a.type]} · {a.category} · {ACCESS_LABEL[a.access]}{sponsorName(a.sponsor_id) ? ` · Sponsored: ${sponsorName(a.sponsor_id)}` : ""}</p>
              <p className="font-display font-semibold mt-1 truncate">{a.title}</p>
              <p className="text-xs text-[#575757] mt-1">{a.takeaways.length} AI takeaways · {a.views || 0} views</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => regen(a)} disabled={busyId === a.id} data-testid={`admin-regen-takeaways-${a.slug}`} className="flex items-center gap-1 border border-[#F7CFCB] bg-[#FFF6F5] px-3 py-2 text-xs"><Sparkles size={13} /> {busyId === a.id ? "Generating…" : "AI takeaways"}</button>
              <button onClick={() => setEditing(a)} data-testid={`admin-edit-${a.slug}`} className="border border-[#E7E7E7] px-3 py-2"><Pencil size={14} /></button>
              <button onClick={() => remove(a)} data-testid={`admin-delete-${a.slug}`} className="border border-[#E7E7E7] px-3 py-2 text-[#E5332A]"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing && <ArticleForm article={editing} sponsors={sponsors} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
};
